import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Stack, Divider, Popover } from '@mui/material';
import { motion } from 'framer-motion';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { Icon, IconGroup } from './details.data';
import { isEmpty } from '../../utils/lodash';
import { StyledBadge } from '../StyledBadge';

interface AddToPostBarProps {
    iconGroups?: IconGroup[];
    mainLabel?: string;
    onIconClick?: (group: Icon) => void;
    onMoreClicked?: (iconGroup: IconGroup) => void;
    disableImage?: boolean;
    requiredFields?: any[];
    state?: Record<string, any>;
    image?: string[];
    selectedColor: string | null;
    currentCategory: string;
    isMobile?: boolean;
}

const AddToPostBar: React.FC<AddToPostBarProps> = ({
    iconGroups,
    mainLabel,
    onIconClick,
    onMoreClicked,
    disableImage,
    requiredFields,
    state = {},
    image,
    selectedColor,
    isMobile = false,
}) => {
    const { themeStyle } = useThemeContext();
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, label: string) => {
        setPopoverAnchor(event.currentTarget);
        setHoveredLabel(label);
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
        setHoveredLabel(null);
    };

    const getBadgeColor = (iconItem: Icon): 'success' | 'error' => {
        if (!iconItem.required || !iconItem.field) return 'error'; // No badge if not required or no fields defined

        if (iconItem.id === 'add_image') {
            // Special validation for 'add_image'
            const hasImageOrColor = (!!image && !isEmpty(image)) || !!selectedColor;
            return hasImageOrColor ? 'success' : 'error'; // Green if valid, red otherwise
        }

        if (!iconItem.field) {
            return 'error';
        }

        // Generic validation for other icons
        const fields = Array.isArray(iconItem.field) ? iconItem.field : [iconItem.field];
        const hasValue = fields.some(
            field => state[field as keyof Record<string, any>] && !isEmpty(state[field as keyof Record<string, any>]),
        );

        return hasValue ? 'success' : 'error'; // Green if at least one value, red otherwise
    };

    const handleScroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 200;
            const currentScroll = scrollContainerRef.current.scrollLeft;
            const newScroll =
                direction === 'left' ? Math.max(0, currentScroll - scrollAmount) : currentScroll + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: newScroll,
                behavior: 'smooth',
            });
        }
    };

    const checkScrollPosition = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
    };

    useEffect(() => {
        checkScrollPosition();
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollPosition);
            return () => scrollContainer.removeEventListener('scroll', checkScrollPosition);
        }
    }, [iconGroups]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#222222',
                borderRadius: '8px',
                padding: '8px 16px',
                color: 'white',
                border: `0.5px solid ${theme[themeStyle].true_white}`,
                mt: 2,
                ...(isMobile && {
                    position: 'relative',
                    overflow: 'hidden',
                    padding: '12px 16px',
                }),
            }}>
            <Typography
                variant="body1"
                sx={{
                    fontWeight: 'bold',
                    fontSize: 12,
                    flexShrink: 0,
                    marginRight: '16px',
                }}>
                {mainLabel}
            </Typography>

            {iconGroups && (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        ...(isMobile && {
                            position: 'relative',
                            overflow: 'hidden',
                        }),
                    }}>
                    {/* Left Arrow for Mobile */}
                    {isMobile && showLeftArrow && (
                        <IconButton
                            onClick={() => handleScroll('left')}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                width: '32px',
                                height: '32px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                },
                            }}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}

                    {/* Right Arrow for Mobile */}
                    {isMobile && showRightArrow && (
                        <IconButton
                            onClick={() => handleScroll('right')}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                width: '32px',
                                height: '32px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                },
                            }}>
                            <ChevronRightIcon />
                        </IconButton>
                    )}

                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            ...(isMobile && {
                                overflowX: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                paddingLeft: showLeftArrow ? '40px' : '0px',
                                paddingRight: showRightArrow ? '40px' : '0px',
                            }),
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                                width: '100%',
                                gap: 1,
                            }}>
                            {iconGroups.map((group, groupIndex) => (
                                <Stack
                                    key={groupIndex}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    divider={
                                        <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#444444' }} />
                                    }
                                    sx={{
                                        flexShrink: 0,
                                        ...(isMobile && {
                                            gap: '12px',
                                            alignItems: 'center',
                                        }),
                                    }}>
                                    {group.icons.slice(0, 5).map((iconItem, iconIndex) => (
                                        <motion.div
                                            key={iconIndex}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}>
                                            {/* StyledBadge wrapping IconButton */}
                                            <StyledBadge
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                errorState={getBadgeColor(iconItem) === 'error'}
                                                variant="dot"
                                                invisible={!iconItem.required} // Show badge only if iconItem.required is true
                                            >
                                                <IconButton
                                                    onClick={() =>
                                                        onIconClick &&
                                                        !(disableImage && iconItem.id === 'add_image') &&
                                                        onIconClick(iconItem)
                                                    }
                                                    onMouseEnter={e => handlePopoverOpen(e, iconItem.label)}
                                                    onMouseLeave={handlePopoverClose}
                                                    disabled={disableImage && iconItem.id === 'add_image'}
                                                    sx={{
                                                        color:
                                                            disableImage && iconItem.id === 'add_image'
                                                                ? theme[themeStyle].deep_grey
                                                                : 'inherit',
                                                        '&:hover': {
                                                            color:
                                                                disableImage && iconItem.id === 'add_image'
                                                                    ? theme[themeStyle].deep_grey
                                                                    : '#00ff7f',
                                                        },
                                                        fontSize: 12,
                                                        ...(isMobile && {
                                                            width: '36px',
                                                            height: '36px',
                                                            minWidth: '36px',
                                                            padding: '6px',
                                                        }),
                                                    }}>
                                                    {iconItem.icon}
                                                </IconButton>
                                            </StyledBadge>
                                        </motion.div>
                                    ))}

                                    {/* "More" Button */}
                                    {group.icons.length > 5 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}>
                                            <IconButton
                                                onClick={() => onMoreClicked && onMoreClicked(group)}
                                                sx={{
                                                    color: 'inherit',
                                                    '&:hover': { color: '#00ff7f' },
                                                    fontSize: 12,
                                                    ...(isMobile && {
                                                        width: '36px',
                                                        height: '36px',
                                                        minWidth: '36px',
                                                        padding: '6px',
                                                    }),
                                                }}>
                                                <MoreHorizIcon />
                                            </IconButton>
                                        </motion.div>
                                    )}
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Popover */}
            <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                disableRestoreFocus
                sx={{
                    pointerEvents: 'none',
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: '#333',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                    },
                }}>
                <Typography variant="caption">{hoveredLabel}</Typography>
            </Popover>
        </Box>
    );
};

export default AddToPostBar;
